/* eslint-disable no-alert */
import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import {
  SpinnerGap, MusicNote, File as FileIcon, CurrencyDollar,
  Users, Warning, CheckCircle, CloudArrowUp, MagnifyingGlass,
} from '@phosphor-icons/react';

const inputCls = "w-full px-3 py-2 border border-[#D1D5DB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] outline-none";
const selectCls = `${inputCls} bg-white`;

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm" data-testid={testid}>
      <h3 className="text-base font-semibold text-[#111827] mb-4 flex items-center gap-2">
        {Icon && <Icon size={18} className="text-[#0F1829]" />} {title}
      </h3>
      {children}
    </div>
  );
}

function Msg({ result }) {
  if (!result) return null;
  const ok = result.success !== false;
  const Icon = ok ? CheckCircle : Warning;
  return (
    <div data-testid="action-result" className={`mt-3 px-3 py-2 rounded border text-sm flex items-start gap-2 ${
      ok ? 'bg-green-50 border-green-300 text-green-700' : 'bg-red-50 border-red-300 text-red-700'
    }`}>
      <Icon size={14} className="mt-0.5 shrink-0" /> {result.message}
    </div>
  );
}

function Btn({ onClick, disabled, loading, children, testid, color = 'primary' }) {
  const cls = color === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white'
    : color === 'success' ? 'bg-green-600 hover:bg-green-700 text-white'
      : 'bg-[#0F1829] hover:bg-[#1E2738] text-white';
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled || loading}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md disabled:opacity-40 whitespace-nowrap ${cls}`}>
      {loading && <SpinnerGap size={14} className="animate-spin" />}
      {children}
    </button>
  );
}

// ─── Upload Vitamina MP3 ────────────────────────────────────────────
function UploadVitamina() {
  const [file, setFile] = useState(null);
  const [descricao, setDescricao] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("descricao", descricao || "");
      const r = await api.post("/backoffice/cdn/upload-vitamina", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({ success: true, message: `Vitamina carregada (#${r.data.ficheiro_id}) · ${Math.round(r.data.size_bytes / 1024)} KB · URL: ${r.data.public_url}`, data: r.data });
      setFile(null); setDescricao("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.detail || e.message || "Erro no upload" });
    } finally { setLoading(false); }
  };

  return (
    <Card title="Carregar Vitamina para a mente na CDN" icon={MusicNote} testid="card-upload-vitamina">
      <p className="text-xs text-[#6B7280] mb-3">Apenas ficheiros MP3. Máximo 100 MB.</p>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-[#6B7280] mb-1">Ficheiro MP3</span>
          <input ref={fileRef} type="file" accept="audio/mpeg,.mp3"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm border border-[#D1D5DB] rounded-md px-3 py-2 file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#0F1829] file:text-white file:text-xs"
            data-testid="vitamina-file" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-[#6B7280] mb-1">Descrição (opcional)</span>
          <input type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)}
            placeholder="ex: Vitamina Diária - Segunda-feira"
            className={inputCls} data-testid="vitamina-descricao" />
        </label>
        <Btn onClick={submit} disabled={!file} loading={loading} testid="vitamina-submit">
          <CloudArrowUp size={14} /> Carregar Vitamina
        </Btn>
        <Msg result={result} />
      </div>
    </Card>
  );
}

// ─── Upload Ficheiro Generic CDN ────────────────────────────────────
function UploadFicheiro() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/backoffice/cdn/upload-ficheiro", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult({
        success: true,
        message: `Ficheiro guardado · ${Math.round(r.data.size_bytes / 1024)} KB`,
        url: r.data.public_url,
        physicalName: r.data.physical_name,
      });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.detail || e.message || "Erro no upload" });
    } finally { setLoading(false); }
  };

  return (
    <Card title="Carregar ficheiro CDN" icon={FileIcon} testid="card-upload-ficheiro">
      <p className="text-xs text-[#6B7280] mb-3">
        Apenas faz upload do ficheiro. <span className="font-medium">Não cria nenhum registo em base de dados</span>.
        Use o URL devolvido para referenciar o ficheiro onde precisar.
      </p>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-[#6B7280] mb-1">Ficheiro</span>
          <input ref={fileRef} type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm border border-[#D1D5DB] rounded-md px-3 py-2 file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#0F1829] file:text-white file:text-xs"
            data-testid="ficheiro-file" />
        </label>
        <Btn onClick={submit} disabled={!file} loading={loading} testid="ficheiro-submit">
          <CloudArrowUp size={14} /> Carregar Ficheiro
        </Btn>
        {result?.success && result.url && (
          <div className="mt-2 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 break-all">
            <p className="font-medium mb-1">{result.message}</p>
            <p className="font-mono text-[10px]">{result.url}</p>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(result.url)}
              className="mt-1 text-[10px] underline hover:no-underline"
              data-testid="ficheiro-copy-url"
            >Copiar URL</button>
          </div>
        )}
        {result && !result.success && <Msg result={result} />}
      </div>
    </Card>
  );
}

// ─── Moeda Default Produtos/Packs ───────────────────────────────────
function MoedaDefault() {
  const [kind, setKind] = useState("produto");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [moeda, setMoeda] = useState("EUR");
  const [moedas, setMoedas] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/backoffice/moeda/list").then((r) => setMoedas(r.data?.data || [])).catch(() => {});
  }, []);

  const search = async () => {
    setSearching(true); setResult(null);
    try {
      const r = await api.get(`/backoffice/moeda/lookup-item?kind=${kind}&query=${encodeURIComponent(query || "")}`);
      setItems(r.data?.data || []);
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.detail || e.message });
    } finally { setSearching(false); }
  };

  const apply = async () => {
    if (!selected) return;
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/backoffice/moeda/set-default", {
        kind, id: selected.id, id_moeda: moeda,
      });
      setResult({ success: true, message: r.data.message });
      // Refresh list to show updated currency
      await search();
      setSelected(null);
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  return (
    <Card title="Definir moeda defeito dos produtos/packs" icon={CurrencyDollar} testid="card-moeda">
      <div className="space-y-3">
        <div className="flex gap-2 items-end flex-wrap">
          <label className="flex-none">
            <span className="block text-xs font-medium text-[#6B7280] mb-1">Tipo</span>
            <select value={kind} onChange={(e) => { setKind(e.target.value); setItems([]); setSelected(null); }}
              className={selectCls} data-testid="moeda-kind">
              <option value="produto">Produto</option>
              <option value="pack">Pack</option>
            </select>
          </label>
          <label className="flex-1 min-w-[200px]">
            <span className="block text-xs font-medium text-[#6B7280] mb-1">Pesquisar {kind}</span>
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="ID ou descrição..."
              className={inputCls} data-testid="moeda-query" />
          </label>
          <Btn onClick={search} loading={searching} testid="moeda-search">
            <MagnifyingGlass size={14} /> Pesquisar
          </Btn>
        </div>

        {items.length > 0 && (
          <div className="border border-[#E5E7EB] rounded overflow-hidden max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] text-xs text-[#6B7280]">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Descrição</th>
                  <th className="px-3 py-2 text-center">Moeda actual</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className={`border-t border-[#F3F4F6] ${selected?.id === it.id ? "bg-[#FAF8F2]" : ""}`}
                    data-testid={`moeda-row-${it.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">#{it.id}</td>
                    <td className="px-3 py-2">{it.label}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{it.moeda_actual || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setSelected(it)}
                        className="text-xs px-2 py-1 rounded border border-[#E6E1D6] hover:bg-[#0F1829] hover:text-white"
                        data-testid={`moeda-select-${it.id}`}>Escolher</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selected && (
          <div className="p-3 bg-[#FAF8F2] border border-[#E6E1D6] rounded">
            <p className="text-xs text-[#6B7280] mb-2">
              A alterar: <strong>#{selected.id}</strong> {selected.label} (moeda actual: <code>{selected.moeda_actual || "—"}</code>)
            </p>
            <div className="flex gap-2 items-end">
              <label className="flex-1">
                <span className="block text-xs font-medium text-[#6B7280] mb-1">Nova moeda</span>
                <select value={moeda} onChange={(e) => setMoeda(e.target.value)}
                  className={selectCls} data-testid="moeda-new">
                  {moedas.map((m) => <option key={m.id} value={m.id}>{m.id} — {m.label}</option>)}
                </select>
              </label>
              <Btn onClick={apply} loading={loading} color="success" testid="moeda-apply">
                Guardar
              </Btn>
              <button onClick={() => setSelected(null)}
                className="px-3 py-2 text-xs text-[#6B7280] hover:text-[#0F1829]">Cancelar</button>
            </div>
          </div>
        )}

        <Msg result={result} />
      </div>
    </Card>
  );
}

// ─── Importação de Clientes ─────────────────────────────────────────
function ImportacaoClientes() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const doPreview = async () => {
    if (!file) return;
    setPreviewing(true); setPreview(null); setApplyResult(null); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/backoffice/clientes/import-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Erro no preview");
    } finally { setPreviewing(false); }
  };

  const doApply = async () => {
    if (!file || !preview) return;
    if (!window.confirm(`Aplicar importação? ${preview.summary.create} clientes novos e ${preview.summary.update} actualizações.`)) return;
    setApplying(true); setApplyResult(null); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/backoffice/clientes/import-apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setApplyResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Erro a aplicar");
    } finally { setApplying(false); }
  };

  const resetAll = () => {
    setFile(null); setPreview(null); setApplyResult(null); setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const ACTIONS = { create: { c: "text-green-700 bg-green-50", l: "Criar" },
    update: { c: "text-blue-700 bg-blue-50", l: "Actualizar" },
    error: { c: "text-red-700 bg-red-50", l: "Erro" } };

  return (
    <Card title="Importação de Ficheiros de Clientes" icon={Users} testid="card-import-clientes">
      <p className="text-xs text-[#6B7280] mb-3">
        Aceita CSV (separador <code>;</code> ou <code>,</code>) e Excel (.xlsx). Cabeçalho obrigatório.
        Se a coluna <strong>ID_Cliente</strong> estiver preenchida, actualiza; senão cria novo cliente (Email + NomeCompleto obrigatórios).
      </p>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-[#6B7280] mb-1">Ficheiro CSV / Excel</span>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setApplyResult(null); }}
            className="w-full text-sm border border-[#D1D5DB] rounded-md px-3 py-2 file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#0F1829] file:text-white file:text-xs"
            data-testid="import-file" />
        </label>

        <div className="flex gap-2 flex-wrap">
          <Btn onClick={doPreview} disabled={!file} loading={previewing} testid="import-preview">
            Pré-visualizar
          </Btn>
          {preview && (
            <Btn onClick={doApply} disabled={applying || !preview.summary || preview.summary.create + preview.summary.update === 0}
              loading={applying} color="success" testid="import-apply">
              Aplicar ({preview.summary.create} novos, {preview.summary.update} updates)
            </Btn>
          )}
          {(preview || applyResult) && (
            <button onClick={resetAll} className="px-3 py-2 text-sm text-[#6B7280] hover:text-[#0F1829]" data-testid="import-reset">
              Limpar
            </button>
          )}
        </div>

        {error && <div className="px-3 py-2 bg-red-50 border border-red-300 text-sm text-red-700 rounded flex items-start gap-2" data-testid="import-error">
          <Warning size={14} className="mt-0.5 shrink-0" /> {error}
        </div>}

        {preview && !applyResult && (
          <div className="border border-[#E5E7EB] rounded" data-testid="import-preview-block">
            <div className="bg-[#FAF8F2] px-3 py-2 border-b border-[#E5E7EB] flex flex-wrap gap-3 text-xs">
              <span><strong>{preview.total_rows}</strong> linhas totais</span>
              <span className="text-green-700"><strong>{preview.summary.create}</strong> novos</span>
              <span className="text-blue-700"><strong>{preview.summary.update}</strong> actualizações</span>
              <span className="text-red-700"><strong>{preview.summary.error}</strong> erros</span>
              {preview.ignored?.length > 0 && (
                <span className="text-[#978D78]">Colunas ignoradas: {preview.ignored.join(", ")}</span>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F9FAFB] text-[#6B7280] sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left w-10">#</th>
                    <th className="px-2 py-1.5 text-left w-24">Acção</th>
                    <th className="px-2 py-1.5 text-left">Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((p) => {
                    const cfg = ACTIONS[p.action] || ACTIONS.error;
                    return (
                      <tr key={p.index} className="border-t border-[#F3F4F6]">
                        <td className="px-2 py-1.5 font-mono text-[#6B7280]">{p.index}</td>
                        <td className="px-2 py-1.5"><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cfg.c}`}>{cfg.l}</span></td>
                        <td className="px-2 py-1.5 text-[#4B5563]">{p.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {applyResult && (
          <div className="border border-[#E5E7EB] rounded" data-testid="import-apply-result">
            <div className="bg-green-50 px-3 py-2 border-b border-[#E5E7EB] flex flex-wrap gap-3 text-xs">
              <CheckCircle size={14} className="text-green-700" />
              <span><strong>{applyResult.total}</strong> linhas processadas</span>
              <span className="text-green-700"><strong>{applyResult.created}</strong> criados</span>
              <span className="text-blue-700"><strong>{applyResult.updated}</strong> actualizados</span>
              <span className="text-red-700"><strong>{applyResult.errors}</strong> erros</span>
            </div>
            {applyResult.errors > 0 && (
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] text-[#6B7280]">
                    <tr>
                      <th className="px-2 py-1.5 text-left w-10">#</th>
                      <th className="px-2 py-1.5 text-left">Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applyResult.results.filter((r) => !r.ok).map((r) => (
                      <tr key={r.index} className="border-t border-[#F3F4F6]">
                        <td className="px-2 py-1.5 font-mono text-[#6B7280]">{r.index}</td>
                        <td className="px-2 py-1.5 text-red-700">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Main Tab ───────────────────────────────────────────────────────
export default function TabCarregamentos() {
  return (
    <div className="space-y-4" data-testid="tab-carregamentos">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UploadVitamina />
        <UploadFicheiro />
      </div>
      <MoedaDefault />
      <ImportacaoClientes />
    </div>
  );
}
